import React, { useCallback, useState } from "react";
import { Avatar, Badge, BmModal, Btn, Field, NoticeState } from "../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import { usePlayerImport } from "../../ForBusiness/Cacheshare/hooks/usePlayerImport";
import { cascadeFieldSpecs, teamDisplayName, useCascadeFields } from "../../ForBusiness/Cacheshare/hooks/useCascadeFields";
import { teamsStatusText } from "../../ForBusiness/Cacheshare/hooks/useTeamCascade";
import { dataStatusNotice, initialsOf, jerseyOf, playerMeta, rowId } from "../../ForBusiness/Cacheshare/allocationModel";
import { fmtIndex10 } from "../../../utils/indexScore";
import { CascadeSelect, RemovableTag, SearchField, useDismiss } from "../../ForBusiness/Cacheshare/v2/allocationUi";
import TeamList, { teamCountLabel } from "../../ForBusiness/Cacheshare/v2/TeamList";

// Coach Mode · CacheShare™ · Add player (Figma 03a): sport and gender come
// from the program, a tier is required, a team is optional, and players
// found by name or position collect as pills until they are imported. On
// phones (2421:2393) it is a full-screen page: the header holds the plain
// title, the "Adding to" line opens the body, the fields and searches are
// 56px, and Import / Cancel stack in the pinned action bar.

// The picked team's level in words, the tier its list came from ("College",
// "Pro", "HS / Grassroots"): coaches never see the data provider (D16).
const levelOf = (team, filters) => (team ? filters.tier?.name || "" : "");

const pillLabel = (p) => `${jerseyOf(p) ? `#${jerseyOf(p)} ` : ""}${p.playerName}`;

// Result lists open in the flow under their input: the dialog clips
// anything positioned outside it.
const Results = ({ children }) => <div className="mt-1">{children}</div>;
const ResultsNote = ({ children }) => <p className="rounded-[12px] border border-bm-border bg-white px-3 py-2 bm-body-s text-bm-muted">{children}</p>;

const useResultsToggle = () => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  return { open, setOpen, close, ref };
};

const TITLE = "Add Player to CacheShare";

// "Adding to: {team} · {sport} · Level {badge}". 8px apart in the dialog
// title, 6px on the phone page (2421:2416).
const ContextLine = ({ destination, sportLabel, level, gap = "gap-2" }) => (
  <div className={`flex flex-wrap items-center ${gap} bm-body-m text-bm-muted`}>
    <span>Adding to:</span>
    <span className="bm-label-l text-bm-ink">{destination || "No team selected"}</span>
    <span className="text-bm-tertiary">·</span>
    <span>{sportLabel}</span>
    {level && (
      <>
        <span className="text-bm-tertiary">·</span>
        <span>Level</span>
        <Badge tone="outline">{level}</Badge>
      </>
    )}
  </div>
);

const Title = (props) => (
  <div className="flex flex-col gap-2">
    <span>{TITLE}</span>
    <ContextLine {...props} />
  </div>
);

// On phones each block (label, field, meta) is 10px apart and the fields
// are 56px (2421:2533, 2421:2555).
const BLOCK = { md: "flex flex-col gap-2", lg: "flex flex-col gap-[10px]" };

const TeamTypeahead = ({ cascade, disabled, size = "md" }) => {
  const { filters, setFilters, filteredTeams, isLoadingTeams, teamsError, teamsErrorText, retryTeams, selectedTeam, setSelectedTeam, scopeName } = cascade;
  const pop = useResultsToggle();
  let status = teamCountLabel(filteredTeams.length, scopeName);
  if (disabled) status = "Choose a tier first";
  else if (isLoadingTeams) status = "Loading teams...";
  else if (teamsError) status = teamsStatusText(teamsErrorText, "Teams did not load for this selection.");
  return (
    <div className={BLOCK[size]}>
      <span className="bm-label-l text-bm-ink">Team · Optional</span>
      <div ref={pop.ref}>
        <SearchField
          value={filters.teamSearch || ""}
          onChange={(v) => {
            setFilters({ ...filters, teamSearch: v });
            pop.setOpen(true);
          }}
          onFocus={() => pop.setOpen(true)}
          placeholder="Search any team…"
          label="Search any team"
          disabled={disabled}
          size={size}
        />
        {pop.open && !disabled && (
          <Results>
            {isLoadingTeams ? (
              <ResultsNote>Loading teams...</ResultsNote>
            ) : filteredTeams.length ? (
              <TeamList
                teams={filteredTeams}
                selectedTeam={selectedTeam}
                onSelect={(t) => {
                  setSelectedTeam(t);
                  pop.close();
                }}
                className="max-h-[260px]"
                size={size}
              />
            ) : (
              <ResultsNote>{teamsError ? teamsStatusText(teamsErrorText, "Teams did not load for this selection. Use Try again below.") : "No teams found for this selection."}</ResultsNote>
            )}
          </Results>
        )}
      </div>
      <div className={size === "lg" ? "flex flex-wrap items-center gap-2" : "flex flex-wrap items-center justify-between gap-2"}>
        <span role={teamsError && !disabled ? "alert" : undefined} className={`bm-body-s ${teamsError && !disabled ? "text-bm-critical" : "text-bm-tertiary"}`}>
          {status}
        </span>
        {teamsError && !disabled && !isLoadingTeams && (
          <Btn variant="secondary" size={size === "lg" ? "lg" : "sm"} onClick={retryTeams}>
            Try again
          </Btn>
        )}
        {selectedTeam && (
          <RemovableTag onRemove={() => setSelectedTeam(null)} removeLabel="Clear the selected team">
            Selected: {teamDisplayName(selectedTeam)}
          </RemovableTag>
        )}
      </div>
    </div>
  );
};

// A player's sport score (the performance pillar) out of 10 with its scale,
// "6.1/10" (utils/indexScore): no column head names it here.
const PlayerOption = ({ player, selected, onToggle }) => (
  <li role="option" aria-selected={selected} className="border-b border-bm-mutedbg last:border-b-0">
    <button type="button" onClick={() => onToggle(player)} className={`flex w-full items-center gap-3 px-3 py-2 text-left ${selected ? "bg-bm-orange/[0.08]" : "hover:bg-bm-subtle"}`}>
      <Avatar initials={initialsOf(player.playerName)} size={28} tone="outline" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate bm-label-l text-bm-ink">{player.playerName}</span>
        <span className="truncate bm-body-s text-bm-tertiary">{[playerMeta(player), player.teamName].filter(Boolean).join(" · ")}</span>
      </span>
      <span className="bm-num-m text-bm-ink">{fmtIndex10(player.sportScore, { suffix: true })}</span>
      <span className="w-4 text-bm-orange" aria-hidden="true">
        {selected ? "✓" : ""}
      </span>
    </button>
  </li>
);

const NCAA_NOTE = "Pick a conference or team to search NCAA D-I.";

// The note under the player field without a team (see usePlayerImport's
// searchState); with a team the roster filters as the coach types.
const searchNote = (state, skipsNcaa) =>
  ({
    short: "Type a name (two letters or more) or a position and press Enter, or pick a team to browse its roster.",
    narrow: NCAA_NOTE,
    pending: `Press Enter to search players by name or position.${skipsNcaa ? ` ${NCAA_NOTE}` : ""}`,
    searching: "Searching players...",
  })[state] || "";

const PlayerPicker = ({ m, disabled, size = "md" }) => {
  const pop = useResultsToggle();
  const team = m.cascade.selectedTeam;
  const message = m.isLoadingRoster ? "Loading roster and scoring players…" : searchNote(m.searchState, m.skipsNcaa) || (m.matches.length ? "" : "No matching players.");
  const count = m.selected.length;
  const meta = disabled
    ? "Choose a tier first"
    : team
      ? `${m.available.length} player${m.available.length === 1 ? "" : "s"} on ${teamDisplayName(team)} · ${count} selected`
      : `${count} selected`;
  return (
    <div className={BLOCK[size]}>
      <span className="bm-label-l text-bm-ink">Player · Search by name or position</span>
      <div ref={pop.ref}>
        <SearchField
          value={m.query}
          onChange={(v) => {
            m.setQuery(v);
            pop.setOpen(true);
          }}
          onFocus={() => pop.setOpen(true)}
          onEnter={() => {
            m.search();
            pop.setOpen(true);
          }}
          placeholder="Search by player name or position…"
          label="Search by player name or position"
          disabled={disabled}
          size={size}
        />
        {pop.open && !disabled && (
          <Results>
            {message ? (
              <ResultsNote>{message}</ResultsNote>
            ) : (
              <ul role="listbox" aria-label="Players" aria-multiselectable="true" className="max-h-[260px] overflow-y-auto rounded-[12px] border border-bm-border bg-white">
                {m.matches.map((p) => (
                  <PlayerOption key={rowId(p)} player={p} selected={m.selectedIds.has(rowId(p))} onToggle={m.toggle} />
                ))}
              </ul>
            )}
          </Results>
        )}
      </div>
      <span className="bm-body-s text-bm-tertiary">{meta}</span>
      {count > 0 && (
        <div className="flex flex-wrap gap-2">
          {m.selected.map((p) => (
            <RemovableTag key={rowId(p)} onRemove={() => m.remove(rowId(p))} removeLabel={`Remove ${p.playerName}`}>
              {pillLabel(p)}
            </RemovableTag>
          ))}
        </div>
      )}
    </div>
  );
};

const AddPlayerDialog = ({ onClose, onImport, destination, defaults, existingIds, programLabel }) => {
  const phone = usePhoneTheme();
  const m = usePlayerImport({ defaults, existingIds });
  const { filters, setFilters, selectedTeam } = m.cascade;
  const spec = cascadeFieldSpecs(useCascadeFields(filters, setFilters), filters);
  const auto = `Auto (${programLabel})`;
  const cells = [
    [spec.sport, auto],
    [spec.gender, auto],
    [spec.tier, "Required"],
    [spec.region, "Optional"],
    [spec.body, "Optional"],
    [spec.subDivision, "Optional"],
    [spec.classDivision, "Optional"],
    [spec.conference, "Optional"],
  ].filter(([s]) => s);
  const sportLabel = filters.sport ? `${filters.sport.name}${filters.gender ? ` (${filters.gender.name})` : ""}` : "No sport";
  const context = { destination, sportLabel, level: levelOf(selectedTeam, filters) };
  const size = phone ? "lg" : "md";
  const count = m.selected.length;
  // The teams, roster or search results come from a paused provider's saved copy.
  const notice = dataStatusNotice(m.dataStatus);
  const importNow = () => {
    onImport(m.selected);
    onClose();
  };

  // Phones: one column of 56px selects 14px apart, labels 8px above them.
  const fields = cells.map(([s, tag]) => (
    <Field key={s.label} label={`${s.label} · ${tag}`} className={phone ? "!gap-2" : undefined}>
      <CascadeSelect spec={s} size={size} />
    </Field>
  ));

  return (
    <BmModal
      width={720}
      onClose={onClose}
      phonePresentation="fullscreen"
      title={phone ? TITLE : <Title {...context} />}
      footer={
        <>
          <Btn variant="secondary" className="w-[125px]" onClick={onClose}>
            Cancel
          </Btn>
          <Btn className="ml-auto w-full max-w-[295px]" disabled={!count} onClick={importNow}>
            Import players{count ? ` (${count})` : ""}
          </Btn>
        </>
      }
    >
      <div className={phone ? "flex flex-col gap-5" : "flex flex-col gap-5 py-1"}>
        {phone && <ContextLine {...context} gap="gap-[6px]" />}
        <p className="bm-body-m text-bm-muted">
          Sport and gender are pre-filled from {programLabel}. Choose a tier (required), then narrow by team if you like, or search athletes directly by
          name or position.
        </p>
        {notice && <NoticeState role="status">{notice}</NoticeState>}
        <div className={phone ? "flex flex-col gap-[14px]" : "grid grid-cols-1 gap-4 sm:grid-cols-2"}>{fields}</div>
        <TeamTypeahead cascade={m.cascade} disabled={!m.ready} size={size} />
        <PlayerPicker m={m} disabled={!m.ready} size={size} />
      </div>
    </BmModal>
  );
};

// Mounted only while open, so every opening starts from the program's
// sport and gender with nothing picked.
const AllocationAddPlayerModal = ({ open, programLabel = "your program", ...props }) => (open ? <AddPlayerDialog programLabel={programLabel} {...props} /> : null);

export default AllocationAddPlayerModal;
